// hooks/useExamens.js
import { useCallback, useRef, useState } from 'react';
import { api } from '../services/api';

/**
 * Accès aux données des examens nationaux.
 *
 * DEUX DÉFAUTS CORRIGÉS ICI
 * -------------------------
 * 1. Un seul `loading` était partagé par les huit méthodes. Comme les écrans
 *    court-circuitent leur rendu quand il est vrai, déposer une pièce faisait
 *    disparaître le dossier entier — deux fois de suite (l'envoi, puis le
 *    rechargement du détail). Chaque famille d'opération a désormais son propre
 *    drapeau, et `chargements.liste` ne bouge plus quand on encaisse.
 *
 * 2. Les huit `catch` renvoyaient `[]`, `null` ou `{ success: false }` sans
 *    jamais prévenir personne : une coupure réseau se lisait « aucun élève ».
 *    Les échecs de lecture passent maintenant par `onErreur`, que l'écran
 *    branche sur son toast.
 */
export const useExamens = ({ onErreur = null } = {}) => {
  const [chargements, setChargements] = useState({
    liste: false,
    details: false,
    ecriture: false,
    statistiques: false
  });
  const [error, setError] = useState(null);

  // `onErreur` change à chaque rendu du parent : le passer en dépendance
  // relancerait les effets qui appellent ces méthodes.
  const rappelErreur = useRef(onErreur);
  rappelErreur.current = onErreur;

  const marquer = useCallback((cle, valeur) => {
    setChargements((precedent) => ({ ...precedent, [cle]: valeur }));
  }, []);

  /**
   * Exécute un appel en tenant son drapeau, et signale l'échec.
   *
   * `valeurParDefaut` est ce que reçoit l'appelant quand ça casse — mais
   * l'erreur, elle, ne se perd plus.
   */
  const executer = useCallback(async (cle, appel, valeurParDefaut, messageEchec) => {
    marquer(cle, true);
    setError(null);
    try {
      const result = await appel();
      if (result?.success) return result;
      throw new Error(result?.message || result?.error || messageEchec);
    } catch (err) {
      setError(err.message);
      rappelErreur.current?.(err.message);
      return valeurParDefaut;
    } finally {
      marquer(cle, false);
    }
  }, [marquer]);

  const getElevesByExamen = useCallback(async (examenType) => {
    const lecteurs = {
      CEPE: api.examens.getElevesCEPE,
      BEPC: api.examens.getElevesBEPC,
      BAC: api.examens.getElevesBAC
    };
    const lecteur = lecteurs[examenType];
    if (!lecteur) {
      rappelErreur.current?.(`Examen inconnu : ${examenType}`);
      return [];
    }
    const result = await executer(
      'liste', lecteur, null, 'Erreur lors de la récupération des élèves'
    );
    return result?.data ?? [];
  }, [executer]);

  const getStatsDashboard = useCallback(async () => {
    const result = await executer(
      'statistiques',
      api.examens.getStatisticsDashboard,
      null,
      'Erreur lors de la récupération des statistiques'
    );
    return result?.data ?? null;
  }, [executer]);

  const getConfig = useCallback(async () => {
    const result = await executer(
      'liste', api.examens.getConfig, null, 'Erreur lors de la lecture de la configuration'
    );
    return result?.data ?? [];
  }, [executer]);

  const getDetailsInscription = useCallback(async (eleveId, examenType) => {
    const result = await executer(
      'details',
      () => api.examens.getDetailsInscription(eleveId, examenType),
      null,
      'Erreur lors de la lecture du dossier'
    );
    return result?.data ?? null;
  }, [executer]);

  const uploadDocument = useCallback(async (eleveId, documentRequisId, file) => {
    // Les écritures ne passent pas par `onErreur` : leur refus est déjà rendu
    // à l'appelant, qui l'affiche avec son propre libellé d'action.
    marquer('ecriture', true);
    setError(null);
    try {
      const result = await api.examens.uploadDocument(eleveId, documentRequisId, file);
      if (!result?.success) {
        setError(result?.message || result?.error || null);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message, error: err.message };
    } finally {
      marquer('ecriture', false);
    }
  }, [marquer]);

  const enregistrerPaiement = useCallback(async (paiementData) => {
    marquer('ecriture', true);
    setError(null);
    try {
      const result = await api.examens.enregistrerPaiement(paiementData);
      if (!result?.success) {
        setError(result?.message || result?.error || null);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message, error: err.message };
    } finally {
      marquer('ecriture', false);
    }
  }, [marquer]);

  const inscrireCandidatLibre = useCallback(async (candidatData) => {
    marquer('ecriture', true);
    setError(null);
    try {
      const result = await api.examens.inscrireCandidatLibre(candidatData);
      if (!result?.success) {
        setError(result?.message || result?.error || null);
      }
      return result;
    } catch (err) {
      setError(err.message);
      return { success: false, message: err.message, error: err.message };
    } finally {
      marquer('ecriture', false);
    }
  }, [marquer]);

  const getCandidatsLibres = useCallback(async (examenType) => {
    const result = await executer(
      'liste',
      () => api.examens.getCandidatsLibres(examenType),
      null,
      'Erreur lors de la récupération des candidats libres'
    );
    return result?.data ?? [];
  }, [executer]);

  return {
    chargements,
    // Conservé pour les appelants qui veulent « quelque chose charge ».
    // Ne JAMAIS s'en servir pour court-circuiter un rendu : c'était l'origine
    // de l'écran qui se vidait à chaque dépôt de pièce.
    enCours: Object.values(chargements).some(Boolean),
    error,
    getElevesByExamen,
    getStatsDashboard,
    getConfig,
    getDetailsInscription,
    uploadDocument,
    enregistrerPaiement,
    inscrireCandidatLibre,
    getCandidatsLibres
  };
};
